import type { Video } from '@nelnela/contracts';
import type { PlaybackEngine } from './engine';

/**
 * Resume position (M6a step 7) — where the learner is inside a video, written while they watch
 * and read back the next time they open it.
 *
 * Two pure rules and one subscriber. The rules are here because both of them are the kind of
 * thing that is obviously right until it is wrong on a phone: whether a position is worth
 * resuming from at all, and how much of the video was actually PLAYED as opposed to how long the
 * screen was open.
 */

/** Below this, "resuming" would rewind the learner to somewhere they had barely started. */
export const RESUME_MIN_MS = 15_000;
/** Within this of the end, the video is finished; resuming there shows credits. */
export const RESUME_TAIL_MS = 30_000;
/** How often to report a position while playing. */
export const PROGRESS_INTERVAL_MS = 10_000;

/**
 * Should Watch open at the stored position?
 *
 * `hasT` wins over everything: an explicit `?t=` is the learner (or a saved-line link, or the
 * bite's watch tile) asking for a specific moment, and quietly landing somewhere else instead
 * would make every deep link in the app unreliable.
 *
 * The tail guard needs the duration, and an upload sometimes reports none until its metadata
 * loads. Without a duration we cannot tell "40 minutes in, of 41" from "40 minutes in, of 90",
 * so the rule is the conservative one: resume, because the position was real. The worst case is
 * landing near the end of something already finished, which the resume bar's "Start over" undoes
 * in one tap.
 */
export function shouldResume(
  progress: Video['progress'] | null | undefined,
  durationMs: number | null | undefined,
  hasT: boolean,
): boolean {
  if (hasT) return false;
  if (!progress) return false;
  const pos = progress.positionMs;
  if (!Number.isFinite(pos) || pos < RESUME_MIN_MS) return false;
  const total = durationMs ?? progress.durationMs ?? null;
  if (total === null || !Number.isFinite(total) || total <= 0) return true;
  return pos < total - RESUME_TAIL_MS;
}

/**
 * The position Watch should CUE at. Zero means "from the start", which is also what the player
 * does with no `startMs` at all.
 *
 * Named separately from `shouldResume` so the call site reads as one expression and so the
 * `?t=` precedence lives in exactly one place.
 */
export function resumeStartMs(
  tParamMs: number,
  progress: Video['progress'] | null | undefined,
  durationMs: number | null | undefined,
): number {
  if (tParamMs > 0) return Math.round(tParamMs);
  return shouldResume(progress, durationMs, false) ? Math.round(progress!.positionMs) : 0;
}

export interface ProgressWriterOptions {
  /** the PUT; injected so a test never touches the network */
  put: (
    videoId: string,
    body: { positionMs: number; durationMs?: number },
    init?: { keepalive?: boolean },
  ) => Promise<unknown>;
  now?: () => number;
  intervalMs?: number;
}

export interface ProgressWriter {
  /** total PLAYING time in ms since the writer started — `ActivityEnd.playedMs` */
  playedMs(): number;
  /** flush the current position now (pause, pagehide, unmount) */
  flush(opts?: { keepalive?: boolean }): void;
  stop(): void;
}

/**
 * Subscribe to the engine and keep `video_progress` roughly current.
 *
 * Deliberately dumb about *when*: every 10 s of playing, plus a flush on every pause and on
 * pagehide. A writer that fired on every tick would be a request per 100 ms, and one that fired
 * only on unmount would lose the position of a tab that was closed — which is most of them.
 *
 * `playedMs` is accumulated from the engine's own `playing` flag rather than from wall clock,
 * because the two are very different numbers and the app needs both: `activity.duration_ms` is
 * wall clock and is what the goal ring counts ("it all counted"), while this is what the watch
 * tile's progress bar counts. A learner who leaves Watch open in another tab has spent the
 * minutes and has not watched the scene.
 *
 * The clock is `now()`, not a timer: a background tab throttles `setInterval` to once a minute,
 * and an accumulator built on ticks would quietly under-count exactly the sessions M5's
 * background-audio work exists to support. Every engine notification measures the interval that
 * has actually elapsed.
 */
export function createProgressWriter(
  engine: PlaybackEngine,
  videoId: string,
  opts: ProgressWriterOptions,
): ProgressWriter {
  const now = opts.now ?? (() => Date.now());
  const intervalMs = opts.intervalMs ?? PROGRESS_INTERVAL_MS;

  let played = 0;
  let lastTick = now();
  let wasPlaying = engine.getState().playing;
  let lastWriteAt = 0;
  let lastWritten = -1;

  const write = (keepalive: boolean): void => {
    const s = engine.getState();
    const positionMs = Math.max(0, Math.round(s.currentMs));
    // Nothing moved: a pause immediately after a flush, or a pagehide on a paused player. The
    // row would be identical, and this fires on a path (pagehide) where the browser is counting
    // our outstanding requests.
    if (positionMs === lastWritten) return;
    lastWritten = positionMs;
    lastWriteAt = now();
    void Promise.resolve(
      opts.put(videoId, { positionMs }, keepalive ? { keepalive: true } : undefined),
    ).catch(() => {
      // A lost position is worth nothing and a thrown one would take the screen with it. The
      // next tick writes again.
      lastWritten = -1;
    });
  };

  const unsubscribe = engine.subscribe(() => {
    const s = engine.getState();
    const t = now();
    // Accumulate the interval that just elapsed, and only if it was spent playing.
    if (wasPlaying) played += Math.max(0, t - lastTick);
    lastTick = t;

    if (wasPlaying && !s.playing) {
      // Pausing is the most useful moment there is to record a position: it is where the
      // learner stopped, and it is the position they will expect to come back to.
      wasPlaying = false;
      write(false);
      return;
    }
    wasPlaying = s.playing;
    if (s.playing && t - lastWriteAt >= intervalMs) write(false);
  });

  return {
    playedMs(): number {
      // Include the interval in flight, or a screen closed 9 s into a clip reports 0.
      const t = now();
      return Math.round(played + (wasPlaying ? Math.max(0, t - lastTick) : 0));
    },
    flush(o): void {
      write(o?.keepalive ?? false);
    },
    stop(): void {
      unsubscribe();
    },
  };
}
